import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  GlAccount,
  CreateAccountData,
  UpdateAccountData,
  AccountsResponse,
  AccountResponse,
} from '../types/account';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getAllAccounts = async (): Promise<GlAccount[]> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ACCOUNTS.GET_ALL}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch accounts');
  }

  const result: AccountsResponse = await response.json();
  return result.data ?? [];
};

export const getAccountById = async (id: number): Promise<GlAccount> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ACCOUNTS.GET_BY_ID}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch account');
  }

  const result: AccountResponse = await response.json();
  return result.data;
};

export const createAccount = async (accountData: CreateAccountData): Promise<GlAccount> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ACCOUNTS.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(accountData),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create account');
  }

  const result: AccountResponse = await response.json();
  return result.data;
};

export const updateAccount = async (
  accountId: number,
  accountData: UpdateAccountData
): Promise<GlAccount> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ACCOUNTS.UPDATE}/${accountId}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(accountData),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update account');
  }

  const result: AccountResponse = await response.json();
  return result.data;
};

export const deleteAccount = async (accountId: number): Promise<void> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ACCOUNTS.DELETE}/${accountId}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete account');
  }
};
